import bus from '@/core/bus';
import { BusConstants, SignalREvents } from '@/core/constants';
import { ISignalRClient } from '@/models/website/SignalRClient';
import { ISignalRMemberCounter } from '@/models/website/SignalRMemberCounter';
import { IWebsiteClient } from '@/models/website/WebsiteClient';
import { BugTrackerModule } from '@/store/modules/bugtracker/bugtracker.store';
import { NewsModule } from '@/store/modules/news/news.store';
import { StatisticsModule } from '@/store/modules/statistics/statistics.store';
import { UserModule } from '@/store/modules/user/user.store';
import {
    BugTrackerIssueDetailsViewModel,
    BugTrackerIssueViewObject,
    GameAccountData,
    NewsCommentReactionViewObject,
    NewsCommentViewObject,
    NewsReactionViewObject,
    NotificationViewObject,
    UserInformationResponse,
    VoteViewObject
} from '@/types/apiServerContract';

import SignalRService from './signalR.service';

SignalRService.addEvent(SignalREvents.UpdateOnlineUsers, (users: IWebsiteClient[], visitors: number, memberCount: ISignalRMemberCounter) => {
    StatisticsModule.SetOnlineUsers({ users, visitors, memberCount });
});

SignalRService.addEvent(SignalREvents.UpdateUserInformations, (response: UserInformationResponse, count: number) => {
    StatisticsModule.UpdateNewestUser({ user: response, count });
});

SignalRService.addEvent(SignalREvents.UpdateNewsComments, (newsId: number, comments: NewsCommentViewObject[]) => {
    NewsModule.UpdateComments({ comments: comments, newsId: newsId });
});

SignalRService.addEvent(SignalREvents.InitializeCurrentSession, (client: ISignalRClient) => {
    UserModule.UpdateCurrentSession(client);
    if (UserModule.user) {
        UserModule.SaveUser(UserModule.user);
    }
});

SignalRService.addEvent(SignalREvents.Notification, (notification: NotificationViewObject) => {
    UserModule.NotificationReceived(notification);
    bus.emit(BusConstants.NotificationReceived, notification);
});

SignalRService.addEvent(SignalREvents.SynchronizeNotifications, (notifications: NotificationViewObject[]) => {
    UserModule.SynchronizeNotifications(notifications);
});

SignalRService.addEvent(SignalREvents.ValidateVersion, (version: string) => {
    const currentVersion = StatisticsModule.websiteVersion;
    if (currentVersion && currentVersion !== version) {
        bus.emit(BusConstants.ReloadWebsite);
        return;
    }
    StatisticsModule.SetWebsiteVersion(version);
});

SignalRService.addEvent(SignalREvents.UpdateUserSessions, (clients: ISignalRClient[], connection: ISignalRClient, lost: boolean) => {
    UserModule.SetActiveConnections(clients);

    if (connection.connectionId !== SignalRService.connectionId) {
        bus.emit(BusConstants.UpdateUserSessions, { connection, lost });
    }
});

SignalRService.addEvent(SignalREvents.LogoutUser, async() => {
    await UserModule.Logout();
    window.location.reload(true);
});

SignalRService.addEvent(SignalREvents.UpdateNewsReaction, (reaction: NewsReactionViewObject) => {
    NewsModule.UpdateNewsReaction(reaction);
});

SignalRService.addEvent(SignalREvents.UpdateNewsCommentReaction, (reaction: NewsCommentReactionViewObject) => {
    NewsModule.UpdateNewsCommentReaction(reaction);
});

SignalRService.addEvent(SignalREvents.BugTrackerIssueSubmitted, (issue: BugTrackerIssueViewObject) => {
    BugTrackerModule.handleIssueCreated(issue);
});

SignalRService.addEvent(SignalREvents.BugTrackerIssueDetailsUpdated, (details: BugTrackerIssueDetailsViewModel, fetchSummaries: boolean) => {
    BugTrackerModule.handleIssueDetailsUpdated(details);
    if (fetchSummaries) {
        BugTrackerModule.getIssuesSummary();
    }
});

SignalRService.addEvent(SignalREvents.BugTrackerIssueRemoved, (issueId: number) => {
    BugTrackerModule.handleIssueRemoved(issueId);
});

SignalRService.addEvent(SignalREvents.SynchronizeAccountData, (data: GameAccountData) => {
    UserModule.SaveAccountData(data);
});

SignalRService.addEvent(SignalREvents.Vote, (vote: VoteViewObject) => {
    bus.emit(BusConstants.VoteSuccess, vote.site);
    UserModule.HandleVoteResponse(vote);
});

SignalRService.addEvent(SignalREvents.PushBuildStatusToClient, (status: string) => {
    bus.emit(BusConstants.PushBuildStatusToClient, status);
});
